import {
  Table, TableBody, TableCell, TableContainer, TableRow, Paper, Box,
  Switch, Typography, MenuItem, AlertColor,
  IconButton, Menu, Tooltip, Checkbox, Divider
} from '@mui/material';
import { Roles } from '../../../Constants/BorgEnums';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  deleteUser, updateUserRole, toggleUserEnabled, forceUserPasswordReset,
  getAllOrganizations, updateUser, getAllUsersDetailed
} from '../../../Services/BorgAPIClient';
import ChangeConfirmationDialog from '../../../Components/ChangeConfirmationDialog';
import moment from 'moment';
import UserStatsHeader from '../../../Components/UserStatsHeader';
import OrgAdminTrialBanner from '../../../Components/Admin/OrgAdminTrialBanner';
import React from 'react';
import { LoggedInUserContext } from '../../../Components/Admin/AdminPageContainer'; // Import context
import { UserAdminPageType, Organization } from '../../../Constants/Types';
import { MoreHoriz } from '@mui/icons-material';
import countries from '../../../assets/countries.json';
import { UserFiltersPopover } from 'src/Components/UserFilterPopover';
import { compareUsers } from './compareUsers';
import { compareStrings } from 'src/Util/TableComparers';
import { useTableSort } from 'src/Hooks/useTableSort';
import { SortableTableHead } from 'src/Components/Admin/TableComponents/SortableTableHead';
import { useTableFilter } from 'src/Hooks/useTableFilter';
import { useMultiSelect } from 'src/Hooks/useMultiSelect';
import InlineDropdown from 'src/Components/Admin/InlineDropDown/InlineDropdown';
import InlineDropdownEditor from 'src/Components/Admin/InlineDropDown/InlineDropdownEditor';

export type UserFilters = {
  organizationIds?: string[];
  roles?: Roles[];
  statuses?: boolean[];
};

moment.updateLocale("en", {
  relativeTime: {
    future: "in %s",
    past: "%s ago",
    s: "a few sec",
    ss: "%d sec",
    m: "1 min",
    mm: "%d min",
    h: "1 hr",
    hh: "%d hr",
    d: "1 day",
    dd: "%d day",
    M: "1 mo",
    MM: "%d mo",
    y: "1 yr",
    yy: "%d yr",
  },
});

type Props = { onNotification: (message: string, notificationType?: AlertColor) => void };
type InlineEditorType = "organization" | "role";

const UsersAdminPage = (props: Props) => {

  const [users, setUsers] = useState<UserAdminPageType[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [openConfirmDeleteDialog, setOpenConfirmDeleteDialog] = useState(false);
  const [openConfirmResetPasswordDialog, setOpenConfirmResetPasswordDialog] = useState(false);
  const [userToChange, setUserToChange] = useState<UserAdminPageType | null>(null);
  const [loadingOrgChange, setLoadingOrgChange] = useState<string | null>(null);
  const [loadingRoleChange, setLoadingRoleChange] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuUser, setMenuUser] = useState<UserAdminPageType | null>(null);
  const [bulkActionsAnchor, setBulkActionsAnchor] = useState<HTMLElement | null>(null);
  const [openConfirmBulkResetDialog, setOpenConfirmBulkResetDialog] = useState(false);
  const [openConfirmBulkDeleteDialog, setOpenConfirmBulkDeleteDialog] = useState(false);
  const [inlineEditor, setInlineEditor] = useState<{
    type: InlineEditorType;
    user: UserAdminPageType;
    anchorEl: HTMLElement;
  } | null>(null);

  const {
    filters,
    setFilters,
    filterAnchor,
    openFilters,
    closeFilters,
    isFiltersOpen,
    filteredItems: filteredUsers,
  } = useTableFilter({
    items: users,
    initialFilters: {} as UserFilters,
    predicate: (user, criteria) => {
      if (criteria.organizationIds?.length && !criteria.organizationIds.includes(user.organizationId)) return false;
      if (criteria.roles?.length && !criteria.roles.includes(user.roles[0])) return false;
      if (criteria.statuses?.length && !criteria.statuses.includes(user.enabled)) return false;
      return true;
    },
  });

  type UserSort = 'username' | 'organization' | 'role' | 'lastActive' | 'status' | 'lastJoinedCourse' | 'nationality';

  const {
    sortedItems: sortedUsers,
    sortBy,
    sortDirection,
    handleSort,
  } = useTableSort<UserAdminPageType, UserSort>(
    filteredUsers,
    'username',
    compareUsers
  );

  const multiSelect = useMultiSelect(sortedUsers, (s) => s.id);

  const loggedInUser = useContext(LoggedInUserContext)?.loggedInUser;
  const isStandalone = process.env.REACT_APP_IS_STANDALONE === 'true';
  const showOrgColumn = loggedInUser?.roles[0] !== Roles.OrganizationAdmin && !isStandalone;

  const fetchUsers = useCallback(async () => {
    if (loggedInUser) {
      try {
        let result: { data: UserAdminPageType[]; };

        if (loggedInUser?.roles[0] === Roles.OrganizationAdmin)
          result = await getAllUsersDetailed(loggedInUser.organizationId);
        else
          result = await getAllUsersDetailed();

        setUsers(result.data);

      } catch (error) {
        console.error('Failed to fetch users:', error);
        props.onNotification("Failed to fetch users", "error");
      }
    }
  }, [loggedInUser]);

  const countActiveFilters = (filters: UserFilters): number =>
    (filters.organizationIds?.length || 0) +
    (filters.roles?.length || 0) +
    (filters.statuses?.length || 0);

  const fetchOrganizations = useCallback(async () => {
    try {
      const result = await getAllOrganizations();
      let sortedData = [...result.data];
      sortedData.sort((a, b) => compareStrings(a.name, b.name));
      setOrganizations(sortedData as Organization[]);
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
      props.onNotification("Failed to fetch organizations", "error");
    }
  }, []);

  //Filtering roles in edit roles list per user Role
  const filteredRoles = Object.values(Roles).filter(value =>
    // an org admin cannot promote a user to a system admin
    !(loggedInUser?.roles[0] === Roles.OrganizationAdmin && value === Roles.SystemAdmin) &&
    // org admins and instructors are not allowed in standalone mode
    !(isStandalone && value === Roles.OrganizationAdmin) &&
    !(isStandalone && value === Roles.Instructor)
  );

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (loggedInUser && loggedInUser.roles[0] !== Roles.OrganizationAdmin)    //Don't fetch Organizations if the user is of the role Organization Admin
      fetchOrganizations();
  }, [loggedInUser]); //loggedInUser needs to be set before organizations can be fetched

  const closeConfirmDeleteDialog = () => {
    setOpenConfirmDeleteDialog(false);
  };

  const closeConfirmResetPasswordDialog = () => {
    setOpenConfirmResetPasswordDialog(false);
  };

  const handleDeleteClick = (user: UserAdminPageType) => {
    const currentUserIdString = localStorage.getItem('userId');
    if (!currentUserIdString) {
      console.error("User ID not found in localStorage");
      return;
    }
    const currentUserId = JSON.parse(currentUserIdString);
    if (user.id === currentUserId) {
      props.onNotification("You cannot delete the logged-in account", "warning");
      return;
    }
    setUserToChange(user);
    setOpenConfirmDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (userToChange) {
      try {
        await deleteUser(userToChange.id);
      }
      catch (error) {
        props.onNotification("Failed to delete user", "error");
      }
      fetchUsers();
    }
    setOpenConfirmDeleteDialog(false);
    setUserToChange(null);
  };

  const handleToggleEnable = async (user: UserAdminPageType) => {
    setUserToChange(user);  //(used to update stats panel) set user being updated 

    try {
      await toggleUserEnabled(user.id);
      fetchUsers(); // Update user list after successful API call
    } catch (error) {
      const errorMessage = (error as { title?: string }).title || 'An unknown error occurred';
      console.error(errorMessage);
      props.onNotification(errorMessage, "error");
    }
    setUserToChange(null); //(used to update stats panel) Clear out user set 
  };

  const handleResetPasswordClick = async (user: UserAdminPageType) => {
    const currentUserIdString = localStorage.getItem('userId');
    if (!currentUserIdString) {
      console.error("User ID not found in localStorage");
      return;
    }
    setUserToChange(user);
    setOpenConfirmResetPasswordDialog(true);
  }

  const handleConfirmResetPassword = async () => {
    if (userToChange) {
      try {
        await forceUserPasswordReset(userToChange.id);
      }
      catch (error) {
        props.onNotification("Failed to reset user password", "error");
      }
      userToChange.resetPasswordPending = true;
    }
    setOpenConfirmResetPasswordDialog(false);
    setUserToChange(null);
  };

  const handleBulkActionsMenuClose = () => {
    setBulkActionsAnchor(null);
  };

  const handleBulkResetPasswordsConfirm = () => {
    setOpenConfirmBulkResetDialog(true);
    handleBulkActionsMenuClose();
  };

  const handleBulkDeleteUsersConfirm = () => {
    setOpenConfirmBulkDeleteDialog(true);
    handleBulkActionsMenuClose();
  };

  const closeBulkResetDialog = () => {
    setOpenConfirmBulkResetDialog(false);
  };

  const closeBulkDeleteDialog = () => {
    setOpenConfirmBulkDeleteDialog(false);
  };

  const handleBulkResetPasswords = async () => {
    try {
      const promises = multiSelect.selectedIds.map(async (userId) => {
        const user = users.find(u => u.id === userId);
        if (user) {
          await forceUserPasswordReset(userId);
          user.resetPasswordPending = true;
        }
      });
      await Promise.all(promises);
      props.onNotification(`Password reset initiated for ${multiSelect.selectedIds.length} users`, "success");
      setUsers([...users]); // Trigger re-render
    } catch (error) {
      props.onNotification("Failed to reset passwords for some users", "error");
    }
    closeBulkResetDialog();
  };

  const handleBulkDeleteUsers = async () => {
    try {
      const loggedInUserId = loggedInUser?.id;
      const usersToDelete = multiSelect.selectedIds.filter(userId => userId !== loggedInUserId);

      if (usersToDelete.length !== multiSelect.selectedIds.length) {
        props.onNotification("Cannot delete your own account", "warning");
      }

      if (usersToDelete.length === 0) {
        closeBulkDeleteDialog();
        return;
      }

      const promises = usersToDelete.map(userId => deleteUser(userId));
      await Promise.all(promises);

      props.onNotification(`Successfully deleted ${usersToDelete.length} users`, "success");
      multiSelect.toggleAll(false);
      fetchUsers();
    } catch (error) {
      props.onNotification("Failed to delete some users", "error");
    }
    closeBulkDeleteDialog();
  };

  const calculateTimeAgo = (time: Date) => {
    return moment(time).fromNow();
  };

  const renderNationality = (nationality?: string | null) => {
    if (!nationality) {
      return 'Not set';
    }

    const normalized = nationality.trim().toLowerCase();

    const country = countries.find(c =>
      c.alpha2?.toLowerCase() === normalized ||
      c.alpha3?.toLowerCase() === normalized ||
      c.name?.toLowerCase() === normalized
    );

    if (!country) {
      return nationality;
    }

    const flagSrc = `/assets/img/flags/${country.alpha2.toLowerCase()}.png`;
    const label = country.alpha3.toUpperCase();

    const content = (
      <span style={{ display: 'flex', alignItems: 'center', gap: '6px', width: 'fit-content' }}>
        <img
          src={flagSrc}
          alt={`${country.name} flag`}
          style={{
            width: 20,
            height: 14,
            objectFit: 'cover',
            borderRadius: '2px',
          }}
        />
        {label}
      </span>
    );

    return (
      <Tooltip title={country.name} arrow>
        {content}
      </Tooltip>
    );
  };

  //Handle function
  const handleOrganizationChange = async (
    user: UserAdminPageType,
    selectedOrganizationId: string
  ) => {
    setLoadingOrgChange(user.id);

    try {
      user.organizationId = selectedOrganizationId;

      await updateUser({
        id: user.id,
        email: user.email,
        organizationId: user.organizationId,
        firstName: user.firstName,
        lastName: user.lastName,
        rankTitle: user.rankTitle,
      });

      await fetchUsers();
    } catch (error) {
      console.error(
        "Failed to update user organization:",
        error
      );

      props.onNotification(
        "Failed to update user organization",
        "error"
      );
    } finally {
      setLoadingOrgChange(null);
    }
  };

  const handleRoleChangeDropdown = async (
    user: UserAdminPageType,
    selectedRole: Roles
  ) => {
    setLoadingRoleChange(user.id);

    try {
      user.roles = [selectedRole];

      await updateUserRole(user.id, selectedRole);

      await fetchUsers();
    } catch (error) {
      console.error("Failed to update user role:", error);
      props.onNotification(
        "Failed to update user role",
        "error"
      );
    } finally {
      setLoadingRoleChange(null);
    }
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    user: UserAdminPageType
  ) => {
    setAnchorEl(event.currentTarget);
    setMenuUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuUser(null);
  };

  const isMenuOpenForUser = (user: UserAdminPageType) => Boolean(anchorEl) && menuUser?.id === user.id;

  type RoleData = {
    full: string;
    short: string;
    ranking: number;
  };

  const RolesData: Record<Roles, RoleData> = {
    [Roles.Restricted]: {
      full: "Restricted",
      short: "Restricted",
      ranking: 1,
    },
    [Roles.User]: {
      full: "User",
      short: "User",
      ranking: 2,
    },
    [Roles.SystemAdmin]: {
      full: "System Administrator",
      short: "SysAdmin",
      ranking: 5,
    },
    [Roles.OrganizationAdmin]: {
      full: "Organization Administrator",
      short: "OrgAdmin",
      ranking: 4,
    },
    [Roles.Instructor]: {
      full: "Instructor",
      short: "Instructor",
      ranking: 3,
    },
  };

  const openInlineEditor = (
    event: React.MouseEvent<HTMLElement>,
    type: InlineEditorType,
    user: UserAdminPageType
  ) => {
    setInlineEditor({
      type,
      user,
      anchorEl: event.currentTarget,
    });
  };

  const closeInlineEditor = () => {
    setInlineEditor(null);
  };

  const roleOptions = (
    inlineEditor?.type === "role" &&
    !filteredRoles.includes(inlineEditor.user.roles[0])
      ? [inlineEditor.user.roles[0], ...filteredRoles]
      : [...filteredRoles]
  )
    .sort((a, b) => RolesData[a].ranking - RolesData[b].ranking)
    .map((role) => ({
      value: role,
      label: RolesData[role].full,
    }));

  const USER_COLUMNS_CONFIG = [
    { id: 'username', label: 'Username', width: '10%' },
    { id: 'organization', label: 'Organization', width: '10%' },
    { id: 'role', label: 'Role', width: '10%' },
    { id: 'nationality', label: 'Nationality', width: '10%' },
    { id: 'lastActive', label: 'Last Active', width: '10%' },
    { id: 'lastJoinedCourse', label: 'Last Joined Course', width: '25%' },
    { id: 'status', label: 'Status', width: '5%' },
  ] as const;

  const columns = useMemo(() => {
    return USER_COLUMNS_CONFIG.filter(col => {
      if (col.id === 'organization' && !showOrgColumn) return false;
      return true;
    });
  }, [showOrgColumn]);

  const isSysAdminRowRestricted = (user: UserAdminPageType) =>
    loggedInUser?.roles[0] !== Roles.SystemAdmin && user.roles[0] === Roles.SystemAdmin;

  return (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Box sx={{
        marginY: 2,
        borderRadius: 1,
        flexShrink: 0
      }}>
        <UserStatsHeader statsChanged={userToChange !== null} onOpenFilters={openFilters} activeFilterCount={countActiveFilters(filters)} />
        <OrgAdminTrialBanner />
      </Box>
      <Box sx={{
        flex: 1,
        overflow: 'hidden',
        borderRadius: 1,
        marginY: 2,
        bgcolor: 'primary.dark',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <TableContainer component={Paper} sx={{ flex: 1, overflow: 'auto' }}>
          <Table stickyHeader>
            <SortableTableHead
              columns={columns}
              sortBy={sortBy}
              sortDirection={sortDirection}
              onSort={handleSort}
              prependCell={
                <TableCell sx={{ width: '5%' }} padding='checkbox'>
                  <Checkbox
                    checked={sortedUsers.length > 0 && sortedUsers.every(u => multiSelect.selectedIds.includes(u.id))}
                    onChange={(e) => multiSelect.toggleAll(e.target.checked)}
                  />
                </TableCell>
              }
              appendCell={
                <>
                  {multiSelect.selectedIds.length === 0 ? (
                    <TableCell sx={{ width: '5%' }} align="center">Actions</TableCell>
                  ) : (
                    <TableCell sx={{ width: '5%' }} align="center">
                      <Tooltip title="Bulk actions">
                        <IconButton
                          onClick={(e) => setBulkActionsAnchor(e.currentTarget)}
                          size="small"
                          sx={{
                            color: Boolean(bulkActionsAnchor) ? 'secondary.main' : 'white'
                          }}
                        >
                          <MoreHoriz />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </>
              }
            />
            <TableBody id="list-users">
              {sortedUsers.map((user, index) => (
                <TableRow
                  key={user.id}
                  style={{
                    backgroundColor: user.enabled ? '' : '#8e5d64',
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={multiSelect.isSelected(user.id)}
                      onClick={(event) => {
                        multiSelect.handleRowSelection(event, user.id, index);
                      }}
                    />
                  </TableCell>
                  <TableCell>{user.username}</TableCell>
                  {(loggedInUser?.roles[0] !== Roles.OrganizationAdmin && !isStandalone) && (
                    <TableCell>
                      <InlineDropdown
                        value={user.organizationId ? organizations.find(org => org.id === user.organizationId)?.name || null : null}
                        onClick={(event) =>
                          openInlineEditor(event, "organization", user)
                        }
                        open={inlineEditor?.type === "organization" && inlineEditor.user.id === user.id}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    <InlineDropdown
                      value={RolesData[user.roles[0]].full}
                      placeholder="Select Role"
                      disabled={
                        loggedInUser?.roles[0] !== Roles.SystemAdmin &&
                        user.roles[0] === Roles.SystemAdmin
                      }
                      onClick={(event) =>
                        openInlineEditor(event, "role", user)
                      }
                      open={inlineEditor?.type === "role" && inlineEditor.user.id === user.id}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {
                        renderNationality(user.nationality)
                      }
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {user.lastActive ? (
                      <Typography variant="body2">
                        {calculateTimeAgo(user.lastActive)}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Never
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {user.lastJoinedSessionID ? (
                      <Typography variant="body2">
                        {user.lastJoinedGroupName}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No course joined
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={user.enabled}
                      color="success"
                      onChange={() => handleToggleEnable(user)}
                      size="small"
                      inputProps={{ "aria-label": user.enabled ? "Enabled" : "Disabled" }}
                      disabled={isSysAdminRowRestricted(user)}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, user)}
                        disabled={isSysAdminRowRestricted(user)}
                        color={isMenuOpenForUser(user) ? "secondary" : "default"}
                      >
                        <MoreHoriz fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <UserFiltersPopover
        anchorEl={filterAnchor}
        open={isFiltersOpen}
        filters={filters}
        onChange={setFilters}
        onClose={closeFilters}
        users={users}
        organizations={organizations}
      />

      {/* Deletion Confirmation Dialog */}
      <ChangeConfirmationDialog
        title="Confirm Delete"
        message={`This action cannot be undone.  Are you sure you want to delete "${userToChange?.username}"?`}
        open={openConfirmDeleteDialog}
        onClose={closeConfirmDeleteDialog}
        onCancel={closeConfirmDeleteDialog}
        onConfirm={handleConfirmDelete}
      />
      {/* Password Reset Confirmation Dialog */}
      <ChangeConfirmationDialog
        title="Confirm Password Reset"
        message={`This action cannot be undone.  Are you sure you want to reset password for "${userToChange?.username}"?`}
        open={openConfirmResetPasswordDialog}
        onClose={closeConfirmResetPasswordDialog}
        onCancel={closeConfirmResetPasswordDialog}
        onConfirm={handleConfirmResetPassword}
      />

      {/* Bulk Reset Passwords Confirmation Dialog */}
      <ChangeConfirmationDialog
        title="Confirm Bulk Password Reset"
        message={`This action will reset passwords for ${multiSelect.selectedIds.length} user${multiSelect.selectedIds.length !== 1 ? 's' : ''}. Are you sure you want to proceed?`}
        open={openConfirmBulkResetDialog}
        onClose={closeBulkResetDialog}
        onCancel={closeBulkResetDialog}
        onConfirm={handleBulkResetPasswords}
      />

      {/* Bulk Delete Users Confirmation Dialog */}
      <ChangeConfirmationDialog
        title="Confirm Bulk User Deletion"
        message={`This action cannot be undone. Are you sure you want to delete ${multiSelect.selectedIds.length} user${multiSelect.selectedIds.length !== 1 ? 's' : ''}?`}
        open={openConfirmBulkDeleteDialog}
        onClose={closeBulkDeleteDialog}
        onCancel={closeBulkDeleteDialog}
        onConfirm={handleBulkDeleteUsers}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {menuUser && !menuUser.resetPasswordPending && (
          <MenuItem
            onClick={() => {
              handleResetPasswordClick(menuUser);
              handleMenuClose();
            }}
          >
            Reset Password
          </MenuItem>
        )}

        {menuUser && menuUser.resetPasswordPending && (
          <MenuItem disabled>
            Password Pending
          </MenuItem>
        )}

        <MenuItem
          onClick={() => {
            handleDeleteClick(menuUser!);
            handleMenuClose();
          }}
          sx={{ color: "error.main" }}
        >
          Delete
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={bulkActionsAnchor}
        open={Boolean(bulkActionsAnchor)}
        onClose={handleBulkActionsMenuClose}
      >
        <MenuItem disabled sx={{ fontWeight: 'bold' }}>
          {multiSelect.selectedIds.length} user{multiSelect.selectedIds.length !== 1 ? 's' : ''} selected
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleBulkResetPasswordsConfirm}>
          Reset passwords
        </MenuItem>
        <MenuItem
          onClick={handleBulkDeleteUsersConfirm}
          sx={{ color: "error.main" }}
        >
          Delete users
        </MenuItem>
      </Menu>

      <InlineDropdownEditor
        anchorEl={inlineEditor?.anchorEl ?? null}
        open={inlineEditor !== null}
        value={
          inlineEditor?.type === "organization"
            ? inlineEditor.user.organizationId
            : inlineEditor?.user.roles[0] ?? null
        }
        options={
          inlineEditor?.type === "organization"
            ? organizations.map(org => ({ value: org.id, label: org.name }))
            : roleOptions
        }
        loading={
          inlineEditor
            ? inlineEditor.type === "organization"
              ? loadingOrgChange === inlineEditor.user.id
              : loadingRoleChange === inlineEditor.user.id
            : false
        }
        searchPlaceholder={
          inlineEditor?.type === "organization"
            ? "Search organizations..."
            : "Search roles..."
        }
        onChange={(value) => {
          if (!inlineEditor) {
            return;
          }

          if (inlineEditor.type === "organization") {
            handleOrganizationChange(inlineEditor.user, value);
          } else {
            handleRoleChangeDropdown(inlineEditor.user, value as Roles);
          }
        }}
        onClose={closeInlineEditor}
      />
    </Box>
  );
}

export default UsersAdminPage;